'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Button, TextField, Typography } from 'lighthouse';

import { OtpInputs } from '@/widgets/OtpInputs';
import Timer from '@/widgets/Timer';

import { convertPersianToEnglish } from '@/utilities/convertPersianToEnglishNumber';
import { saveDataToStorage } from '@/utilities/dataStorage';
import { getInAppFlag } from '@/utilities/isInApp';
import { isAppPwaHost } from '@/utilities/pwa';

import { CLIENT_TYPE_CUSTOMER_APP, CLIENT_TYPE_ID, TOKEN_EXPIRE_TIME } from '@/constants/common';
import {
    testValidations,
    VALIDATION_ONLY_PERSIAN_CHARACTERS_MESSAGE,
    VALIDATION_REQUIRED_FILED_MESSAGE,
    validationRegex
} from '@/constants/validations';

import { FormValues, validationCodeStepProps } from './models';

import { requestOtpService, verifyOtpService } from '@/client/services/core/auth/methods';
import { cornbar } from 'cornbar';
import { useFormValidation } from 'hooks/common/useFormValidation';
import { Edit2, Lock1 } from 'iconsax-react';

const initialValues: FormValues = { firstName: '', lastName: '' };

const ValidationCodeStep = ({ phoneNumber, onNextStep, isUserRegistered, onOtpVerified }: validationCodeStepProps) => {
    const [code, setCode] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const isSubmittingRef = useRef(false);

    const validationRules = {
        firstName: (value: string) => {
            if (!value) {
                return VALIDATION_REQUIRED_FILED_MESSAGE;
            } else if (!testValidations(validationRegex.onlyPersianCharacters, value)) {
                return VALIDATION_ONLY_PERSIAN_CHARACTERS_MESSAGE;
            } else {
                return '';
            }
        },
        lastName: (value: string) => {
            if (!value) {
                return VALIDATION_REQUIRED_FILED_MESSAGE;
            } else if (!testValidations(validationRegex.onlyPersianCharacters, value)) {
                return VALIDATION_ONLY_PERSIAN_CHARACTERS_MESSAGE;
            } else {
                return '';
            }
        }
    };

    const { values, validate, errors, handleChange } = useFormValidation(initialValues, validationRules);

    const handleResendOtpRequest = useCallback(async () => {
        return requestOtpService({ phoneNumber });
    }, [phoneNumber]);

    const handleSubmit = () => {
        if (isSubmittingRef.current) {
            return;
        }

        if (!isUserRegistered && !validate()) {
            return;
        }

        if (!code) {
            cornbar.show({
                title: 'خطا رخ داد',
                description: 'لطفا کد پیامک شده را وارد کنید.',
                variant: 'error'
            });
            return;
        }

        isSubmittingRef.current = true;
        setIsLoading(true);
        const isCustomerApp = Boolean(getInAppFlag()) || isAppPwaHost();

        let bodyParam = {
            phoneNumber: convertPersianToEnglish(phoneNumber),
            otpCode: code,
            clientType: isCustomerApp ? CLIENT_TYPE_CUSTOMER_APP : CLIENT_TYPE_ID
        };

        if (!isUserRegistered) {
            bodyParam = { ...bodyParam, ...values };
        }

        verifyOtpService(bodyParam)
            .then((response) => {
                saveDataToStorage('tokens', response.data, 'cookie', TOKEN_EXPIRE_TIME);
                onNextStep('register');
                try {
                    onOtpVerified?.();
                } catch {
                    return;
                }
            })
            .catch(() => {
                isSubmittingRef.current = false;
                setIsLoading(false);
            });
    };

    useEffect(() => {
        if (isUserRegistered && code.length === 4) {
            handleSubmit();
        }
    }, [code, isUserRegistered]);

    return (
        <>
            <Typography variant='subtitle2' className='flex items-center text-base font-bold text-blackHighEmphasis'>
                <Lock1 color='currentcolor' size={22} className='ml-2' />
                اعتبارسنجی
                <Timer onResend={handleResendOtpRequest} />
            </Typography>
            <Typography variant='caption' className='mt-5 text-sm leading-7 text-blackMediumEmphasis' display='block'>
                کد ۴ رقمی به شماره‌ی شما پیامک شد.
            </Typography>

            <button type='button' className='all-unset cursor-pointer' onClick={() => onNextStep('phoneNumber')}>
                <Typography className='mb-8 mt-3 text-sm text-blackHighEmphasis' display='block'>
                    شماره شما: &nbsp;{phoneNumber}
                    <Edit2 color='currentcolor' size={16} className='mr-2' />
                </Typography>
            </button>

            <OtpInputs onSubmit={(otpCode) => setCode(otpCode)} inputsLength={4} />
            {!isUserRegistered && (
                <>
                    <TextField
                        name='firstName'
                        required
                        error={'firstName' in errors}
                        helperText={'firstName' in errors ? errors.firstName : ''}
                        fullWidth
                        inputProps={{
                            maxLength: 100
                        }}
                        size='small'
                        onChange={handleChange}
                        label='نام خود را وارد کنید'
                        className='mt-7'
                    />
                    <TextField
                        name='lastName'
                        required
                        error={'lastName' in errors}
                        helperText={'lastName' in errors ? errors.lastName : ''}
                        fullWidth
                        inputProps={{
                            maxLength: 100
                        }}
                        size='small'
                        onChange={handleChange}
                        label='نام خانوادگی خود را وارد کنید'
                        className='mt-4'
                    />
                </>
            )}
            <Button className='mt-12' loading={isLoading} variant='contained' onClick={handleSubmit} disabled={!code} fullWidth size='large'>
                تایید
            </Button>
        </>
    );
};

export default ValidationCodeStep;
